import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  getCurrentStatus,
  getIsWorkflowSaving,
  getIsWorkflowSaved,
  getWorkflowData,
  selectValidationResults,
  selectValidationFeedback,
  selectAIQCLimit,
  selectSpeakerAttributionResults,
  selectSpeakerAttributionLimit,
  selectCallType,
  selectIsAutomationReview,
  validateTranscript,
  validateSpeakerAttribution,
} from "store/editor";
import SaveWorkflowModal from "../saveWorkflowModal";
import CheckInProgressModal from "../aiQualityCheck/modal";
import SpeakerAttributionModal from "../speakerAttribution/modal";
import CoverageWarningModal from "../components/coverageWarningModal";
import IntegrityFailureModal from "../components/integrityFailureModal";
import {
  makeIssueKey,
  removeResolvedSet,
  buildFeedbackResolvedSet,
} from "../aiQualityCheck/helpers";
import {
  loadResolvedSet as loadSAResolvedSet,
  makeIssueKey as makeSAIssueKey,
  removeResolvedSet as removeSAResolvedSet,
} from "../speakerAttribution/helpers";
import { isUserApplicant, isUserAdmin, getUserInfo } from "utils/userHelpers";
import { showToast } from "store/toastSlice";
import { getCoverageData, removeCoverage, getTotalPlayed } from "../hooks/useEditorCoverageTracker";
import { subscribeChunkedGate, getGateSnapshot } from "../hooks/chunkedExposureGate";
import { sendCoverageWarningNotification } from "utils/enterpriseApi";
import { getPartnerCodeName, isExpertNetworkCall } from "pages/enterpriseDashboard/utils";

export const MAX_AI_QC_ATTEMPTS = 5;
export const MAX_SPEAKER_ATTRIBUTION_ATTEMPTS = 5;
export const TRIGGERS = {
  APPLICANT: {
    AI_QC: null, // "Compliance", disabled for applicants on staging
    SPEAKER_VERIFICATION: null, // "Compliance", disabled for applicants on staging
    SPEAKER_ATTRIBUTION: null,
  },

  // For first draft final files, trigger AI QC and Speaker Verification on QA_Review
  APPLICANT_FDF: {
    AI_QC: null, // "QA_Review", disabled for applicants on staging
    SPEAKER_VERIFICATION: null, // "QA_Review", disabled for applicants on staging
    SPEAKER_ATTRIBUTION: null,
  },

  EDITOR: {
    AI_QC: "QA_Review",
    SPEAKER_VERIFICATION: "First_Review",
    SPEAKER_ATTRIBUTION: "First_Review",
  },

  // For first draft final files, trigger AI QC on QA_Review and Speaker Verification on First_Review
  EDITOR_FDF: {
    AI_QC: "QA_Review",
    SPEAKER_VERIFICATION: "First_Review",
    SPEAKER_ATTRIBUTION: "First_Review",
  },
};
const SPEAKER_ATTRIBUTION_FIRST_RUN_ENABLED = process.env.REACT_APP_SPEAKER_ATTRIBUTION_FIRST_RUN_ENABLED === "true";

export default function NavButtons({ setOpenQcDrawer, openQcDrawer, setOpenSaDrawer, readOnly }) {
  const dispatch = useDispatch();
  const isApplicant = isUserApplicant();
  const shouldAutoRender = useRef(true);
  const shouldAutoRenderSA = useRef(true);

  const workflowData = useSelector(getWorkflowData);
  const workflowId = workflowData?.id ?? null;
  const workflowStatus = useSelector(getCurrentStatus);
  const isSaving = useSelector(getIsWorkflowSaving);
  const isSaved = useSelector(getIsWorkflowSaved);
  const validationResults = useSelector(selectValidationResults);
  const validationFeedback = useSelector(selectValidationFeedback);
  const aiQCLimit = useSelector(selectAIQCLimit);
  const speakerAttributionResults = useSelector(selectSpeakerAttributionResults);
  const saLimit = useSelector(selectSpeakerAttributionLimit);
  const callType = useSelector(selectCallType);
  const isAutomationReview = useSelector(selectIsAutomationReview);

  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [speakerVerified, setSpeakerVerified] = useState(false);
  const [aiQcCancelled, setAiQcCancelled] = useState(false);
  const [saCancelled, setSaCancelled] = useState(false);
  const [svCancelled, setSvCancelled] = useState(false);

  // Reactive subscription to the chunked-exposure gate so the Complete
  // button's `disabled` state updates as chunks unlock. Hook is stable;
  // when the feature is off the gate stays `enabled: false` and the
  // `chunksLocked` flag below is permanently false.
  const gateState = useSyncExternalStore(
    subscribeChunkedGate,
    getGateSnapshot,
    getGateSnapshot
  );
  const chunksLocked = !isUserAdmin() && gateState.enabled && gateState.locked;
  const [qcLoading, setQcLoading] = useState(false);
  const [saLoading, setSaLoading] = useState(false);
  const [qcTriggered, setQCTriggered] = useState(false);
  const [showCoverageWarningModal, setShowCoverageWarningModal] = useState(false);
  const [showIntegrityFailureModal, setShowIntegrityFailureModal] = useState(false);
  const [, setCoverageDataRef] = useState(null);
  // Expert-network calls (call_type variants, or Tegus / Third Bridge / ProSapient partners) skip AI speaker verification.
  const callTypeNorm = (callType || "").toLowerCase();
  const isExpertCall =
    callTypeNorm === "expert call" ||
    callTypeNorm === "expert_call" ||
    isExpertNetworkCall(workflowData?.assignees);
  let aiQCTrigger = (isApplicant && workflowStatus === TRIGGERS.APPLICANT.AI_QC) ||
    (isApplicant === false && workflowStatus === TRIGGERS.EDITOR.AI_QC);
  let speakerVerificationTrigger = (isApplicant && workflowStatus === TRIGGERS.APPLICANT.SPEAKER_VERIFICATION) ||
    (isApplicant === false && workflowStatus === TRIGGERS.EDITOR.SPEAKER_VERIFICATION && isExpertCall === false);
  let speakerAttributionTrigger = (isApplicant && workflowStatus === TRIGGERS.APPLICANT.SPEAKER_ATTRIBUTION) ||
    (isApplicant === false && workflowStatus === TRIGGERS.EDITOR.SPEAKER_ATTRIBUTION);

  if (workflowData?.upload_type === "FIRST_DRAFT") {
    if (isApplicant) {
      aiQCTrigger = (workflowStatus === TRIGGERS.APPLICANT_FDF.AI_QC);
      speakerVerificationTrigger = (workflowStatus === TRIGGERS.APPLICANT_FDF.SPEAKER_VERIFICATION);
      speakerAttributionTrigger = (workflowStatus === TRIGGERS.APPLICANT_FDF.SPEAKER_ATTRIBUTION);
    } else {
      aiQCTrigger = (workflowStatus === TRIGGERS.EDITOR_FDF.AI_QC);
      speakerVerificationTrigger = (workflowStatus === TRIGGERS.EDITOR_FDF.SPEAKER_VERIFICATION && isExpertCall === false);
      speakerAttributionTrigger = (workflowStatus === TRIGGERS.EDITOR_FDF.SPEAKER_ATTRIBUTION);
    }
  }

  // Automation-review files use pre-computed AI QC + speaker attribution; speaker verification still runs live.
  if (isAutomationReview) {
    aiQCTrigger = false;
    speakerAttributionTrigger = false;
  }

  const qcCallback = useCallback((loading) => {
    setOpenQcDrawer(!loading);
    setQcLoading(loading);
  }, [setOpenQcDrawer]);

  const checkTranscriptQuality = useCallback(() => {
    setAiQcCancelled(false);
    removeResolvedSet(workflowId);
    qcCallback(true);
    dispatch(validateTranscript({ callback: () => qcCallback(false) }));
  }, [dispatch, qcCallback, workflowId]);

  const saCallback = useCallback((loading) => {
    if (setOpenSaDrawer) setOpenSaDrawer(!loading);
    setSaLoading(loading);
  }, [setOpenSaDrawer]);

  const checkSpeakerAttribution = useCallback(() => {
    setSaCancelled(false);
    removeSAResolvedSet(workflowId);
    saCallback(true);
    dispatch(validateSpeakerAttribution({ callback: () => saCallback(false) }));
  }, [dispatch, saCallback, workflowId]);

  const onCompleteClick = useCallback(() => {
    // Only run AI QC for applicants on the Compliance stage
    // For everyone else, run AI QC always
    if (aiQCTrigger && !aiQcCancelled) {
      // First complete click: trigger AI validation only
      if (!validationResults && aiQCLimit < MAX_AI_QC_ATTEMPTS) {
        checkTranscriptQuality();
        return;
      } else {
        if (!validationResults && aiQCLimit >= MAX_AI_QC_ATTEMPTS) {
          dispatch(
            showToast({
              message: "QA Quality Check maximum number of attempts reached, skipping...",
              severity: "warning",
            }),
          );
        }
      }

      const resolvedSet = buildFeedbackResolvedSet(validationFeedback);
      const unresolvedCount = (validationResults || []).filter((item) => {
        const key = makeIssueKey({
          category: item.category,
          entity: item.entity,
          startTime: item.start_time,
        });

        return !resolvedSet.has(key);
      }).length;

      if (unresolvedCount > 0) {
        if (setOpenQcDrawer) setOpenQcDrawer(true);
        dispatch(
          showToast({
            message: `Please address ${unresolvedCount} validation issue${unresolvedCount > 1 ? "s" : ""
              } before completing`,
            severity: "error",
            vertical: "top",
          })
        );

        return;
      }
    }

    if (setOpenSaveModal) {
      setOpenSaveModal(true);
    }
    // validationFeedback MUST stay in this list: resolving a flag mutates only the feedback
    // (the flag stays in validationResults), so without it this callback keeps a stale feedback
    // snapshot and the gate re-counts already-resolved flags while the drawer shows none open.
  }, [dispatch, aiQCLimit, aiQCTrigger, aiQcCancelled, validationResults, validationFeedback, checkTranscriptQuality, setOpenQcDrawer]);

  // Continues the complete flow after coverage check passes
  const proceedAfterCoverage = useCallback(() => {
    // Speaker Attribution Check runs before Speaker Verification
    if (SPEAKER_ATTRIBUTION_FIRST_RUN_ENABLED && speakerAttributionTrigger && !saCancelled) {
      if (!speakerAttributionResults && saLimit < MAX_SPEAKER_ATTRIBUTION_ATTEMPTS) {
        checkSpeakerAttribution();
        return;
      } else {
        if (!speakerAttributionResults && saLimit >= MAX_SPEAKER_ATTRIBUTION_ATTEMPTS) {
          dispatch(
            showToast({
              message: "Speaker Attribution Check maximum number of attempts reached, skipping...",
              severity: "warning",
            }),
          );
        }
      }

      const resolvedSet = loadSAResolvedSet(workflowId);
      const unresolvedCount = (speakerAttributionResults || []).filter((item) => {
        const key = makeSAIssueKey({
          category: item.category,
          entity: item.entity,
          startTime: item.start_time,
        });

        return !resolvedSet.has(key);
      }).length;

      if (unresolvedCount > 0) {
        if (setOpenSaDrawer) setOpenSaDrawer(true);
        dispatch(
          showToast({
            message: `Please address ${unresolvedCount} speaker attribution issue${unresolvedCount > 1 ? "s" : ""
              } before completing`,
            severity: "error",
            vertical: "top",
          })
        );

        return;
      }
    }

    if (!speakerVerified && speakerVerificationTrigger && !svCancelled) {
      const event = new CustomEvent("editor.speakers.open", {
        detail: {
          showButton: true,
          automaticCheck: true,
        },
      });

      dispatchEvent(event);

      return;
    } else {
      onCompleteClick();
    }
  }, [speakerVerified, speakerVerificationTrigger, svCancelled, speakerAttributionTrigger, saCancelled, speakerAttributionResults, saLimit, workflowId, onCompleteClick, checkSpeakerAttribution, setOpenSaDrawer, dispatch]);

  const onCoverageWarningSubmit = useCallback(() => {
    setShowCoverageWarningModal(false);
    setCoverageDataRef(null);

    // Coverage data is NOT removed here - it's cleaned up only after updateWorkflow succeeds
    proceedAfterCoverage();
  }, [proceedAfterCoverage]);

  const onCompleteCheck = useCallback(() => {
    // Coverage check runs first, before speaker validation
    const admin = isUserAdmin();
    const applicant = isUserApplicant();

    // Block Submit-to-Compliance when any chunk is still locked.
    if (!admin) {
      const g = getGateSnapshot();
      if (g.enabled && g.locked) {
        dispatch(
          showToast({
            message: `Cannot submit. ${g.totalChunks - g.currentChunkIndex} of ${g.totalChunks} chunks still locked. Play all audio and scroll through the full transcript first.`,
            severity: "error",
            vertical: "top",
          })
        );
        return;
      }
    }

    if (!admin && !applicant) {
      window.dispatchEvent(new CustomEvent("coverage:setIntegrity"));

      const coverage = getCoverageData(workflowData?.id, workflowStatus);

      if (coverage) {
        coverage.workflowTitle = workflowData?.title || null;
        coverage.workflowStatus = workflowStatus;
        coverage.uploadType = workflowData?.upload_type || null;
        // editCount is already set by the coverage tracker hook as delta (version - initialVersion)
        coverage.durationSeconds = workflowData?.duration_seconds || 0;
        coverage.jobType = workflowData?.job_type || null;
        coverage.partnerName = getPartnerCodeName(workflowData?.assignees) || null;
        const userInfo = getUserInfo();
        coverage.team = userInfo?.user?.team || null;
        // Read the live gate snapshot, not the closure-captured `gateState`:
        // this callback is memoized without `gateState` in deps, so the
        // captured value reflects the gate at callback-creation time (often
        // maxUnlockedChunkIndex=0 right after mount). Submission can fire
        // many ticks later after chunks have unlocked. Persisting the stale
        // value would record the wrong resume cursor and downgrade the
        // gated_completion flag.
        const liveGate = getGateSnapshot();
        coverage.gated_completion = !!liveGate.enabled;
        // Persist ratchet so reload resumes the same cursor.
        coverage.max_unlocked_chunk_index = liveGate.maxUnlockedChunkIndex || 0;

        const totalPlayed = getTotalPlayed(coverage.playedSegments, coverage.audioDuration);
        const audioPercent = coverage.audioDuration > 0
          ? (totalPlayed / coverage.audioDuration) * 100
          : 0;
        const viewedCount = Math.min(coverage.viewedSnippetIds.length, coverage.totalSnippetCount);
        const scrollPercent = coverage.totalSnippetCount > 0
          ? (viewedCount / coverage.totalSnippetCount) * 100
          : 0;

        // Always send coverage data to backend (backend decides Slack)
        sendCoverageWarningNotification(coverage).catch((e) =>
          console.error("Coverage notification error:", e)
        );

        // Show warning modal for suspicious activity
        const lowAudio = workflowStatus === "First_Review"
          ? audioPercent < 50
          : audioPercent < 85;
        const lowScroll = scrollPercent < 85;
        const editsInStage = coverage.editCount || 0;
        const lowEdits = workflowStatus === "QA_Review" && editsInStage < 40;
        const integrityFailed = coverage.integrity_check === false;
        const reassignEnabled = process.env.REACT_APP_INTEGRITY_FAIL_REASSIGN_ENABLED === "true";

        // Automation-review flow reviews AI flags, not full coverage: skip the suspicious-activity modals.
        if (!isAutomationReview) {
          if (reassignEnabled && integrityFailed) {
            // If paragraph integrity check fails, show warning modal and exit to dashboard immediately.
            // This only happens if file re-assignment is enabled
            setShowIntegrityFailureModal(true);

            return;
          }

          if (lowAudio || lowScroll || lowEdits || (!reassignEnabled && integrityFailed)) {
            // When immediate file reassignment is disabled, show a unified coverage warning modal.
            setCoverageDataRef(coverage);
            setShowCoverageWarningModal(true);

            return;
          }
        }
        // Coverage data is NOT removed here - it's cleaned up only after updateWorkflow succeeds
      }
    }

    proceedAfterCoverage();
  }, [workflowStatus, workflowData, dispatch, proceedAfterCoverage, isAutomationReview]);

  useEffect(() => {
    if (
      validationResults?.length > 0 &&
      aiQCTrigger &&
      shouldAutoRender.current
    ) {
      setOpenQcDrawer(true);

      shouldAutoRender.current = false;
    }
  }, [validationResults, workflowStatus, isApplicant, aiQCTrigger, setOpenQcDrawer]);

  useEffect(() => {
    const onSpeakerVerified = () => {
      const event = new CustomEvent("closeSpeakerVerification");

      dispatchEvent(event);

      setSpeakerVerified(true);
    };

    window.addEventListener("speakerVerificationComplete", onSpeakerVerified);

    return () => {
      window.removeEventListener("speakerVerificationComplete", onSpeakerVerified);
    }
  }, []);

  useEffect(() => {
    const onSvCancelled = () => setSvCancelled(true);

    window.addEventListener("editor.speakers.verificationCancelled", onSvCancelled);

    return () => {
      window.removeEventListener("editor.speakers.verificationCancelled", onSvCancelled);
    };
  }, []);

  // Listen for AI QC complete event
  useEffect(() => {
    const onAIQCComplete = () => {
      onCompleteCheck();
    };

    window.addEventListener("editor.aiqc.complete", onAIQCComplete);

    return () => {
      window.removeEventListener("editor.aiqc.complete", onAIQCComplete);
    }
  }, [onCompleteCheck]);

  // Listen for AI QC recheck event
  useEffect(() => {
    const onAIQCRecheck = () => {
      checkTranscriptQuality();
    };

    window.addEventListener("editor.aiqc.recheck", onAIQCRecheck);

    return () => {
      window.removeEventListener("editor.aiqc.recheck", onAIQCRecheck);
    };
  }, [checkTranscriptQuality]);

  // Listen for Speaker Attribution complete event
  useEffect(() => {
    const onSAComplete = () => {
      onCompleteCheck();
    };

    window.addEventListener("editor.sa.complete", onSAComplete);

    return () => {
      window.removeEventListener("editor.sa.complete", onSAComplete);
    };
  }, [onCompleteCheck]);

  // Listen for Speaker Attribution recheck event
  useEffect(() => {
    const onSARecheck = () => {
      checkSpeakerAttribution();
    };

    window.addEventListener("editor.sa.recheck", onSARecheck);

    return () => {
      window.removeEventListener("editor.sa.recheck", onSARecheck);
    };
  }, [checkSpeakerAttribution]);

  // Auto-open Speaker Attribution drawer when results arrive from a previous session
  useEffect(() => {
    if (
      speakerAttributionResults?.length > 0 &&
      speakerAttributionTrigger &&
      shouldAutoRenderSA.current
    ) {
      if (setOpenSaDrawer) setOpenSaDrawer(true);

      shouldAutoRenderSA.current = false;
    }
  }, [speakerAttributionResults, speakerAttributionTrigger, setOpenSaDrawer]);

  useEffect(() => {
    // This is for auto-triggering after automatic speaker validation
    if (speakerVerified && !qcTriggered) {
      setQCTriggered(() => true);
      onCompleteClick();
    }
  }, [speakerVerified, qcTriggered, onCompleteClick]);

  // Clean up coverage data only after workflow update succeeds
  useEffect(() => {
    if (isSaved && workflowId && workflowStatus) {
      const userInfo = getUserInfo();
      removeCoverage(workflowId, userInfo?.user?.user_id, workflowStatus);
    }
  }, [isSaved, workflowId, workflowStatus]);

  return (
    <>
      {Array.isArray(validationResults) && validationResults.length > 0 && (
        <Button
          disableElevation
          variant="outlined"
          onClick={() => setOpenQcDrawer(!openQcDrawer)}
          sx={{
            fontSize: "14px",
            padding: "10px 16px",
            textTransform: "none",
            color: openQcDrawer ? "#0659F9" : "inherit",
            borderColor: openQcDrawer ? "#0659F9" : "#E3E6EA",
            "&:hover": { borderColor: "#0659F9", color: "#0659F9" },
          }}
        >
          AI QC
        </Button>
      )}

      <LoadingButton
        disableElevation
        disabled={readOnly || isSaving || (workflowStatus === "Publication") || chunksLocked}
        loading={qcLoading || saLoading || isSaving}
        sx={{
          fontSize: "14px",
          padding: "10px 16px",
          color: "#FFF",
          backgroundColor: "#0659F9",
          "&:disabled": {
            color: "#CACFD5",
            backgroundColor: "#777777",
          },
        }}
        variant="contained"
        onClick={onCompleteCheck}
      >
        {workflowStatus === "Publication" ? "Completed" : "Complete"}
      </LoadingButton>

      <CheckInProgressModal
        loading={qcLoading}
        onCancel={() => {
          setAiQcCancelled(true);
          qcCallback(false);
        }}
      />
      {SPEAKER_ATTRIBUTION_FIRST_RUN_ENABLED && (
        <SpeakerAttributionModal
          loading={saLoading}
          onCancel={() => {
            setSaCancelled(true);
            saCallback(false);
          }}
        />
      )}
      <SaveWorkflowModal open={openSaveModal} setOpen={setOpenSaveModal} />

      {/* Coverage warning (audio playback, scroll depth etc.) modal */}
      <CoverageWarningModal
        open={showCoverageWarningModal}
        onClose={() => {
          setShowCoverageWarningModal(false);
          setCoverageDataRef(null);
        }}
        onSubmit={onCoverageWarningSubmit}
      />

      {/* Paragraph integrity check failure modal */}
      <IntegrityFailureModal open={showIntegrityFailureModal} />
    </>
  );
}
